'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["error", "field", "filterFamily", "options", "resolver", "specialOptions", "onBlur", "onChange"];
import { List, Map as ImmutableMap } from 'immutable';
import { listOf, recordOf } from 'react-immutable-proptypes';
import { INBOUND_DB_LIST } from 'customer-data-objects/property/ExternalOptionTypes';
import FilterFieldType from '../propTypes/FilterFieldType';
import FilterOperatorErrorType from 'customer-data-filters/components/propTypes/FilterOperatorErrorType';
import PropTypes from 'prop-types';
import PropertyOptionRecord from 'customer-data-objects/property/PropertyOptionRecord';
import ReferenceInputEnum from 'customer-data-reference-ui-components/ReferenceInputEnum';
import ReferenceResolverType from 'reference-resolvers/schema/ReferenceResolverType';
import UIFormControl from 'UIComponents/form/UIFormControl';
import isEmpty from 'transmute/isEmpty';
import I18n from 'I18n';
import { getTransferableProps } from './filterInputProps';
import { isExternalOptionsField } from '../../utilities/isExternalOptionsField';
import FilterOperatorExternalOptionInput from './FilterOperatorExternalOptionInput';
import ReferenceResolverLiteType from 'reference-resolvers-lite/components/proptypes/ReferenceResolverLiteType';
import virtualizedMenuRenderer from 'UIComponents/input/utils/virtualizedMenuRenderer';
import { useState } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
const tryConvertMapToPropertyOptionRecord = m => {
  const propertyOption = ImmutableMap.isMap(m) ? m.toJS() : m;
  return PropertyOptionRecord(propertyOption);
};
const getOptions = (options, specialOptions, resolver) => {
  // Some resolvers supply a map. When a map is provided the label is lost,
  // so we must convert them to PropertyOptionRecords
  const safeOptions = options && options.map(tryConvertMapToPropertyOptionRecord);
  if (resolver) {
    return List.isList(specialOptions) ? specialOptions : List();
  }
  if (List.isList(safeOptions) && List.isList(specialOptions)) {
    return specialOptions.concat(safeOptions);
  }
  if (isEmpty(safeOptions) && List.isList(specialOptions)) {
    return specialOptions;
  }
  return safeOptions || List();
};
const getRequestOptions = (objectTypeId, field) => {
  if (field && field.get('referencedObjectType') === INBOUND_DB_LIST) {
    return {
      listFilterProperties: {
        includeDeletes: false,
        objectTypeId
      }
    };
  }
  return undefined;
};
export default function FilterOperatorEnumInput(_ref) {
  let {
      error,
      field,
      filterFamily,
      options,
      resolver,
      specialOptions,
      onBlur,
      onChange
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [isOpen, setIsOpen] = useState(false);
  const isError = error.get('error');
  const errorMessage = error.get('message');
  const onOpenChange = evt => {
    setIsOpen(evt.target.value);
    if (evt.target.value === false && onBlur) {
      onBlur();
    }
  };
  const onChangeLocal = evt => {
    onChange(evt, isOpen);
  };
  const uiFormControlProps = {
    'aria-label': I18n.text('customerDataFilters.FilterOperatorInput.ariaLabel'),
    error: isError,
    validationMessage: isError ? errorMessage : null
  };
  const inputProps = Object.assign({}, getTransferableProps(rest, {
    multi: true
  }), {
    menuRenderer: virtualizedMenuRenderer,
    onChange: onChangeLocal,
    onOpenChange,
    options: getOptions(options, specialOptions, resolver),
    resolver,
    // HACK: prevent ESC key presses from propagating up and cancelling changes
    // See https://git.hubteam.com/HubSpot/UIComponents/issues/9877
    onKeyDown: e => e.stopPropagation(),
    onKeyPress: e => e.stopPropagation(),
    onKeyUp: e => e.stopPropagation()
  });
  if (isExternalOptionsField(field, filterFamily)) {
    return /*#__PURE__*/_jsx(FilterOperatorExternalOptionInput, Object.assign({}, inputProps, {
      uiFormControlProps: uiFormControlProps
    }));
  } else {
    return /*#__PURE__*/_jsx(UIFormControl, {
      "aria-label": uiFormControlProps['aria-label'],
      error: uiFormControlProps.error,
      validationMessage: uiFormControlProps.validationMessage,
      children: /*#__PURE__*/_jsx(ReferenceInputEnum, Object.assign({}, inputProps, {
        requestOptions: getRequestOptions(filterFamily, field)
      }))
    });
  }
}
FilterOperatorEnumInput.propTypes = {
  error: FilterOperatorErrorType.isRequired,
  field: FilterFieldType,
  filterFamily: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  options: listOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  })),
  resolver: PropTypes.oneOfType([ReferenceResolverType, ReferenceResolverLiteType]),
  specialOptions: listOf(recordOf({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  }))
};